import React, { useEffect, useMemo, useState } from "react";
import jwt_decode from "jwt-decode";
import { Card, Row, Col, Form, Button, Spinner, Table } from "react-bootstrap";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { getVentas } from "../helpers/rutaVentas";
import { getPagosVenta } from "../helpers/rutaPagoVentas";
import { getFinancieras } from "../helpers/rutaFinancieras";

import "../css/dashboard-financiero.css";

const todayLocalISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();
  d.setDate(1);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const clean = (v) => String(v ?? "").trim();

const getRoleUsuario = () => {
  const token =
    localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    return (
      decoded?.usuario?.role ||
      decoded?.user?.role ||
      decoded?.role ||
      ""
    );
  } catch {
    return "";
  }
};

const getSedeUsuario = () => {
  const token =
    localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    const usuario =
      decoded?.usuario ||
      decoded?.user ||
      decoded ||
      {};

    return clean(usuario?.sede)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toUpperCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return "";
  }
};

const esVentaMiamiPropia = (
  venta = {}
) => {
  const sede = clean(venta?.sede)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return sede === "MIAMI";
};

const filtrarVentasDashboard = (
  ventas = []
) => {
  const role = getRoleUsuario();
  const sede = getSedeUsuario();

  const esManagerMiami =
    role === "MANAGER_ROLE" &&
    sede === "MIAMI";

  if (!esManagerMiami) {
    return ventas;
  }

  return ventas.filter(
    (venta) =>
      esVentaMiamiPropia(venta)
  );
};

const formatDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const TAX_PORCENTAJE_DEFAULT = 7;

const valorDefinido = (...valores) =>
  valores.find(
    (valor) =>
      valor !== undefined &&
      valor !== null &&
      valor !== ""
  );

const calcularDatosTax = ({
  ventaPago = {},
  venta = {},
  resumenPago = {},
  totalVenta = 0,
}) => {
  const taxPorcentaje = Number(
    valorDefinido(
      resumenPago.taxPorcentaje,
      ventaPago.taxPorcentaje,
      venta.taxPorcentaje,
      TAX_PORCENTAJE_DEFAULT
    )
  );

  const taxGuardado = valorDefinido(
    resumenPago.taxMontoUSD,
    ventaPago.taxMontoUSD,
    venta.taxMontoUSD
  );

  const sinTaxGuardado = valorDefinido(
    resumenPago.totalSinTaxUSD,
    ventaPago.totalSinTaxUSD,
    venta.totalSinTaxUSD
  );

  const taxMontoUSD =
    taxGuardado !== undefined
      ? round2(Number(taxGuardado || 0))
      : round2(
          Number(totalVenta || 0) *
            (taxPorcentaje / 100)
        );

  const totalSinTaxUSD =
    sinTaxGuardado !== undefined
      ? round2(Number(sinTaxGuardado || 0))
      : round2(
          Number(totalVenta || 0) - taxMontoUSD
        );

  return {
    taxPorcentaje,
    taxMontoUSD,
    totalSinTaxUSD,
  };
};

const nombreClienteCompra = (cliente) => {
  if (!cliente) return "";
  return (
    cliente.razonsocial ||
    [cliente.apellido1, cliente.apellido2, cliente.nombre]
      .filter(Boolean)
      .join(" ") ||
    ""
  );
};

const nombreUsuario = (usuario) => {
  if (!usuario) return "";
  if (typeof usuario === "string") return "";
  return (
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    [usuario.nombre, usuario.apellido].filter(Boolean).join(" ") ||
    usuario.email ||
    ""
  );
};

const nombreVendedorVenta = (venta) => {
  return (
    venta?.vendedorNombre ||
    venta?.usuarioNombre ||
    nombreUsuario(venta?.vendedor) ||
    nombreUsuario(venta?.usuario) ||
    ""
  );
};

const nombreFinanciera = (financiera) => {
  if (!financiera) return "";
  if (typeof financiera === "string") return financiera;
  return financiera.financiera || financiera.razonsocial || "";
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const esPagoCreditoFinanciera = (fila = {}) =>
  clean(fila.medioPago).toUpperCase() === "CREDITO FINANCIERA";

const procesarEnLotes = async (items, size, fn) => {
  const resultados = [];
  for (let i = 0; i < items.length; i += size) {
    const lote = items.slice(i, i + size);
    const res = await Promise.all(lote.map(fn));
    resultados.push(...res);
  }
  return resultados;
};

const COLORS = ["#22c55e", "#facc15", "#3b82f6", "#a855f7", "#f97316"];

export default function DashboardFinancieroVentas() {
  const [desde, setDesde] = useState(firstDayMonthISO());
  const [hasta, setHasta] = useState(todayLocalISO());

  const [estadoVenta, setEstadoVenta] = useState("");
  const [estadoPago, setEstadoPago] = useState("");
  const [medioPago, setMedioPago] = useState("");
  const [estadoAcreditacion, setEstadoAcreditacion] = useState("");
  const [financieraFiltro, setFinancieraFiltro] = useState("");
  const [q, setQ] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filas, setFilas] = useState([]);
  const [financieras, setFinancieras] = useState([]);

  const [totales, setTotales] = useState({
    cantidadVentas: 0,
    cantidadPagos: 0,
    totalVentasUSD: 0,
    totalTaxUSD: 0,
    totalSinTaxUSD: 0,
    totalCargadoUSD: 0,
    totalFeeUSD: 0,
    totalMontoFinancieraUSD: 0,
    totalAcreditadoUSD: 0,
    totalPendienteAcreditarUSD: 0,
    saldoPendienteUSD: 0,
  });

  const cargarDashboard = async () => {
    setLoading(true);
    setError("");

    try {
      const LIMIT = 200;
      let page = 1;
      let total = 0;
      let ventas = [];

      do {
        const resp = await getVentas({
          page,
          limit: LIMIT,
          q: "",
          estado: estadoVenta,
          desde,
          hasta,
        });

        const lote = Array.isArray(resp?.ventas) ? resp.ventas : [];
        ventas = ventas.concat(lote);
        total = Number(resp?.total || lote.length || 0);
        page += 1;
        } while (ventas.length < total);

        ventas =
          filtrarVentasDashboard(
            ventas
          );

        const respFinancieras = await getFinancieras({
          page: 1,
          limit: 500,
        });
      setFinancieras(
        Array.isArray(respFinancieras?.financieras)
          ? respFinancieras.financieras
          : []
      );

      const filasPorVenta = await procesarEnLotes(ventas, 8, async (venta) => {
        const ventaId = venta._id || venta.id;
        if (!ventaId) return [];

        let respPagos = null;
        try {
          respPagos = await getPagosVenta(ventaId);
        } catch (e) {
          console.warn("No se pudieron leer pagos de venta:", venta.nroventa, e);
        }

        const pagos = Array.isArray(respPagos?.pagos) ? respPagos.pagos : [];
        const ventaPago = respPagos?.venta || venta;
        const resumenPago = respPagos?.resumenPago || {};

        const totalVenta = Number(
          resumenPago.totalVenta ?? ventaPago.totalUSD ?? venta.totalUSD ?? 0
        );

        const totalPagadoUSD = Number(
          resumenPago.totalPagadoUSD ??
            ventaPago.totalPagadoUSD ??
            venta.totalPagadoUSD ??
            0
        );

        const totalAcreditadoUSD = Number(
          resumenPago.totalAcreditadoUSD ??
            ventaPago.totalAcreditadoUSD ??
            venta.totalAcreditadoUSD ??
            0
        );

        const saldoPendienteUSD =
          resumenPago.saldoPendienteUSD !== undefined &&
          resumenPago.saldoPendienteUSD !== null
            ? Number(resumenPago.saldoPendienteUSD || 0)
            : Math.max(totalVenta - totalAcreditadoUSD, 0);

        const estadoPagoVenta =
          resumenPago.estadoPago || ventaPago.estadoPago || "PENDIENTE";

        const {
          taxPorcentaje,
          taxMontoUSD,
          totalSinTaxUSD,
        } = calcularDatosTax({
          ventaPago,
          venta,
          resumenPago,
          totalVenta,
        });

        const base = {
          ventaId,
          nroventa: ventaPago.nroventa || venta.nroventa,
          fechaVenta: ventaPago.fecha || venta.fecha,
          clienteCompra: ventaPago.clienteCompra || venta.clienteCompra,
          vendedorNombre: nombreVendedorVenta(ventaPago || venta),
          estadoVenta: ventaPago.estado || venta.estado,
          estadoPago: estadoPagoVenta,
          totalVentaUSD: totalVenta,
          taxPorcentaje,
          taxMontoUSD,
          totalSinTaxUSD,
          totalPagadoUSD,
          totalAcreditadoUSD,
          saldoPendienteUSD,
        };

        if (pagos.length === 0) {
          return [
            {
              ...base,
              pagoId: "",
              nropago: "",
              fechaPago: "",
              medioPago: "SIN PAGOS",
              financiera: null,
              financieraNombre: "",
              montoUSD: 0,
              cuotasFinanciacion: 0,
              feePorcentaje: 0,
              feeMontoUSD: 0,
              montoFinancieraUSD: 0,
              estadoAcreditacion: "PENDIENTE",
              fechaAcreditacion: "",
              observacionesPago: "",
            },
          ];
        }

        return pagos.map((pago) => ({
          ...base,
          pagoId: pago._id || pago.id,
          nropago: pago.nropago,
          fechaPago: pago.fechaPago,
          medioPago: pago.medioPago,
          financiera: pago.financiera,
          financieraNombre: nombreFinanciera(pago.financiera),
          montoUSD: Number(pago.montoUSD || 0),
          cuotasFinanciacion: Number(pago.cuotasFinanciacion || 0),
          feePorcentaje: Number(pago.feePorcentaje || 0),
          feeMontoUSD: Number(pago.feeMontoUSD || 0),
          montoFinancieraUSD: Number(pago.montoFinancieraUSD || 0),
          estadoAcreditacion: pago.estadoAcreditacion,
          fechaAcreditacion: pago.fechaAcreditacion,
          observacionesPago: pago.observaciones,
        }));
      });

      setFilas(filasPorVenta.flat());
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al cargar dashboard"
      );
      setFilas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filasFiltradas = useMemo(() => {
    const texto = clean(q).toUpperCase();

    return filas.filter((fila) => {
      if (estadoPago && fila.estadoPago !== estadoPago) return false;
      if (medioPago && fila.medioPago !== medioPago) return false;
      if (estadoAcreditacion && fila.estadoAcreditacion !== estadoAcreditacion)
        return false;

      if (financieraFiltro) {
        const idFin = getId(fila.financiera);
        if (idFin !== financieraFiltro) return false;
      }

      if (texto) {
        const cadena = [
          fila.nroventa,
          nombreClienteCompra(fila.clienteCompra),
          fila.vendedorNombre,
          fila.estadoVenta,
          fila.estadoPago,
          fila.medioPago,
          fila.financieraNombre,
          fila.estadoAcreditacion,
          fila.observacionesPago,
        ]
          .join(" ")
          .toUpperCase();

        if (!cadena.includes(texto)) return false;
      }

      return true;
    });
  }, [
    filas,
    q,
    estadoPago,
    medioPago,
    estadoAcreditacion,
    financieraFiltro,
  ]);

  useEffect(() => {
    const ventasMap = new Map();

    filasFiltradas.forEach((fila) => {
      if (!ventasMap.has(fila.ventaId)) {
        ventasMap.set(fila.ventaId, fila);
      }
    });

    const ventasUnicas = Array.from(ventasMap.values());

    const totalVentasUSD = ventasUnicas.reduce(
      (acc, fila) => acc + Number(fila.totalVentaUSD || 0),
      0
    );

    const totalTaxUSD = ventasUnicas.reduce(
      (acc, fila) =>
        acc + Number(fila.taxMontoUSD || 0),
      0
    );

    const totalSinTaxUSD = ventasUnicas.reduce(
      (acc, fila) =>
        acc + Number(fila.totalSinTaxUSD || 0),
      0
    );

    const saldoPendienteUSD = ventasUnicas.reduce(
      (acc, fila) => acc + Number(fila.saldoPendienteUSD || 0),
      0
    );

    const pagosReales = filasFiltradas.filter((f) => f.pagoId);

    const totalCargadoUSD = pagosReales.reduce(
      (acc, fila) => acc + Number(fila.montoUSD || 0),
      0
    );

    const pagosFinanciera = pagosReales.filter(esPagoCreditoFinanciera);

    const totalFeeUSD = pagosFinanciera.reduce(
      (acc, fila) => acc + Number(fila.feeMontoUSD || 0),
      0
    );

    const totalMontoFinancieraUSD = pagosFinanciera.reduce(
      (acc, fila) => acc + Number(fila.montoFinancieraUSD || 0),
      0
    );

    const totalAcreditadoUSD = pagosReales
      .filter((f) => f.estadoAcreditacion === "ACREDITADO")
      .reduce((acc, fila) => acc + Number(fila.montoUSD || 0), 0);

    const totalPendienteAcreditarUSD = pagosReales
      .filter((f) => f.estadoAcreditacion === "PENDIENTE")
      .reduce((acc, fila) => acc + Number(fila.montoUSD || 0), 0);

    setTotales({
      cantidadVentas: ventasUnicas.length,
      cantidadPagos: pagosReales.length,
      totalVentasUSD: round2(totalVentasUSD),
      totalTaxUSD: round2(totalTaxUSD),
      totalSinTaxUSD: round2(totalSinTaxUSD),
      totalCargadoUSD: round2(totalCargadoUSD),
      totalFeeUSD: round2(totalFeeUSD),
      totalMontoFinancieraUSD: round2(totalMontoFinancieraUSD),
      totalAcreditadoUSD: round2(totalAcreditadoUSD),
      totalPendienteAcreditarUSD: round2(totalPendienteAcreditarUSD),
      saldoPendienteUSD: round2(saldoPendienteUSD),
    });
  }, [filasFiltradas]);

  const serieEntreFechas = useMemo(() => {
    const mapaVentas = new Map();
    const ventasUnicas = new Map();

    const obtenerPunto = (fecha) => {
      if (!mapaVentas.has(fecha)) {
        mapaVentas.set(fecha, {
          fecha,
          ventas: 0,
          tax: 0,
          sinTax: 0,
          cobrado: 0,
          fee: 0,
        });
      }

      return mapaVentas.get(fecha);
    };

    filasFiltradas.forEach((fila) => {
      if (!ventasUnicas.has(fila.ventaId)) {
        ventasUnicas.set(fila.ventaId, fila);
      }
    });

    Array.from(ventasUnicas.values()).forEach((fila) => {
      const fecha = formatDate(fila.fechaVenta) || "-";
      const punto = obtenerPunto(fecha);

      punto.ventas += Number(fila.totalVentaUSD || 0);
      punto.tax += Number(fila.taxMontoUSD || 0);
      punto.sinTax += Number(fila.totalSinTaxUSD || 0);
    });

    filasFiltradas
      .filter(
        (fila) =>
          fila.pagoId &&
          fila.estadoAcreditacion === "ACREDITADO"
      )
      .forEach((fila) => {
        const fecha =
          formatDate(
            fila.fechaAcreditacion || fila.fechaPago
          ) || "-";

        const punto = obtenerPunto(fecha);

        punto.cobrado += Number(fila.montoUSD || 0);
      });

    filasFiltradas
      .filter(
        (fila) =>
          fila.pagoId &&
          esPagoCreditoFinanciera(fila)
      )
      .forEach((fila) => {
        const fecha =
          formatDate(
            fila.fechaPago || fila.fechaVenta
          ) || "-";

        const punto = obtenerPunto(fecha);

        punto.fee += Number(fila.feeMontoUSD || 0);
      });

    return Array.from(mapaVentas.values())
      .map((item) => ({
        ...item,
        ventas: round2(item.ventas),
        tax: round2(item.tax),
        sinTax: round2(item.sinTax),
        cobrado: round2(item.cobrado),
        fee: round2(item.fee),
      }))
      .sort((a, b) => {
        const [da, ma, ya] = a.fecha.split("/");
        const [db, mb, yb] = b.fecha.split("/");

        return (
          new Date(`${ya}-${ma}-${da}`) -
          new Date(`${yb}-${mb}-${db}`)
        );
      });
  }, [filasFiltradas]);

  const ventasPorFinanciera = useMemo(() => {
    const map = new Map();

    filasFiltradas
      .filter((fila) => fila.pagoId && esPagoCreditoFinanciera(fila))
      .forEach((fila) => {
        const key = fila.financieraNombre || "SIN FINANCIERA";

        if (!map.has(key)) {
          map.set(key, {
            financiera: key,
            total: 0,
            fee: 0,
            neto: 0,
            movimientos: 0,
          });
        }

        const item = map.get(key);

        item.total += Number(fila.montoUSD || 0);
        item.fee += Number(fila.feeMontoUSD || 0);
        item.neto += Number(fila.montoFinancieraUSD || 0);
        item.movimientos += 1;
      });

    return Array.from(map.values())
      .map((item) => ({
        ...item,
        total: round2(item.total),
        fee: round2(item.fee),
        neto: round2(item.neto),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [filasFiltradas]);

  // const pendientesPorFinanciera = useMemo(() => {
  //   const map = new Map();

  //   filasFiltradas
  //     .filter((fila) => fila.estadoAcreditacion === "PENDIENTE")
  //     .forEach((fila) => {
  //       const key = fila.financieraNombre || "SIN FINANCIERA";
  //       if (!map.has(key)) {
  //         map.set(key, { financiera: key, total: 0 });
  //       }
  //       map.get(key).total += Number(fila.montoUSD || 0);
  //     });

  //   return Array.from(map.values())
  //     .sort((a, b) => b.total - a.total)
  //     .slice(0, 8);
  // }, [filasFiltradas]);

  const feePorFinanciera = useMemo(() => {
    return ventasPorFinanciera
      .filter((item) => Number(item.fee || 0) > 0)
      .map((item) => ({
        financiera: item.financiera,
        fee: item.fee,
      }))
      .sort((a, b) => b.fee - a.fee)
      .slice(0, 8);
  }, [ventasPorFinanciera]);

  const donutEstado = useMemo(() => {
    return [
      { name: "Acreditado", value: totales.totalAcreditadoUSD },
      { name: "Pendiente", value: totales.totalPendienteAcreditarUSD },
      { name: "Saldo", value: totales.saldoPendienteUSD },
    ].filter((x) => Number(x.value || 0) > 0);
  }, [totales]);

  const topFinancieras = useMemo(() => {
    const totalGeneral =
      ventasPorFinanciera.reduce((acc, item) => acc + Number(item.total || 0), 0) ||
      1;

    return ventasPorFinanciera.map((item, index) => ({
      ...item,
      index: index + 1,
      porcentaje: ((Number(item.total || 0) / totalGeneral) * 100).toFixed(1),
      porcentajeFee:
        Number(item.total || 0) > 0
          ? ((Number(item.fee || 0) / Number(item.total || 0)) * 100).toFixed(1)
          : "0.0",
      ventas: item.movimientos,
    }));
  }, [ventasPorFinanciera]);

  const ultimosMovimientos = useMemo(() => {
    return [...filasFiltradas]
      .filter((f) => f.pagoId)
      .sort((a, b) => {
        const fa = new Date(a.fechaAcreditacion || a.fechaPago || 0).getTime();
        const fb = new Date(b.fechaAcreditacion || b.fechaPago || 0).getTime();
        return fb - fa;
      })
      .slice(0, 6);
  }, [filasFiltradas]);

  const limpiarFiltros = () => {
    setDesde(firstDayMonthISO());
    setHasta(todayLocalISO());
    setEstadoVenta("");
    setEstadoPago("");
    setMedioPago("");
    setEstadoAcreditacion("");
    setFinancieraFiltro("");
    setQ("");
  };

  return (

     <div className="dashboard-financiero dashboard-light container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Dashboard Financiero de Ventas</h3>
          <small className="text-muted">
            Resumen general del desempeño financiero de ventas
          </small>
        </div>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={limpiarFiltros}>
            Limpiar
          </Button>
          <Button variant="dark" size="sm" onClick={cargarDashboard} disabled={loading}>
            {loading ? "Buscando..." : "Buscar"}
          </Button>
        </div>
      </div>

      <Row className="g-2 mb-3">
        <Col md={2}>
          <Form.Label>Desde</Form.Label>
          <Form.Control type="date" size="sm" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </Col>

        <Col md={2}>
          <Form.Label>Hasta</Form.Label>
          <Form.Control type="date" size="sm" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </Col>

        <Col md={2}>
          <Form.Label>Estado pago</Form.Label>
          <Form.Select size="sm" value={estadoPago} onChange={(e) => setEstadoPago(e.target.value)}>
            <option value="">Todos</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="PARCIAL">PARCIAL</option>
            <option value="PAGADO">PAGADO</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Label>Medio pago</Form.Label>
          <Form.Select size="sm" value={medioPago} onChange={(e) => setMedioPago(e.target.value)}>
            <option value="">Todos</option>
            <option value="SIN PAGOS">SIN PAGOS</option>
            <option value="EFECTIVO EN TIENDA">EFECTIVO EN TIENDA</option>
            <option value="ZELLE">ZELLE</option>
            <option value="DEBITO">DEBITO</option>
            <option value="CREDITO">CREDITO</option>
            <option value="CREDITO FINANCIERA">CREDITO FINANCIERA</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Label>Acreditación</Form.Label>
          <Form.Select size="sm" value={estadoAcreditacion} onChange={(e) => setEstadoAcreditacion(e.target.value)}>
            <option value="">Todas</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="ACREDITADO">ACREDITADO</option>
            <option value="ANULADO">ANULADO</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Label>Financiera</Form.Label>
          <Form.Select size="sm" value={financieraFiltro} onChange={(e) => setFinancieraFiltro(e.target.value)}>
            <option value="">Todas</option>
            {financieras.map((f) => (
              <option key={f._id || f.id} value={f._id || f.id}>
                {f.financiera || f.razonsocial || f._id}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Row xs={1} md={2} xl={4} className="g-3 mb-3 dashboard-kpis">
            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-blue">US$</div>
                  <small>Venta total</small>
                  <h4>{formatUSD(totales.totalVentasUSD)}</h4>
                  <div className="kpi-sub">{totales.cantidadVentas} ventas</div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-blue">7%</div>

                  <small>Tax</small>

                  <h4 className="text-primary">
                    {formatUSD(totales.totalTaxUSD)}
                  </h4>

                  <div className="kpi-sub">
                    Impuesto incluido en ventas
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-blue">US$</div>

                  <small>Venta sin tax</small>

                  <h4 className="text-primary">
                    {formatUSD(totales.totalSinTaxUSD)}
                  </h4>

                  <div className="kpi-sub">
                    Venta real sin impuesto
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-green">💵</div>
                  <small>Total cargado</small>
                  <h4>{formatUSD(totales.totalCargadoUSD)}</h4>
                  <div className="kpi-sub">{totales.cantidadPagos} pagos</div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi dashboard-kpi-fee">
                <Card.Body>
                  <div className="kpi-icon kpi-red">%</div>
                  <small>Fee retenido</small>
                  <h4 className="text-fee">{formatUSD(totales.totalFeeUSD)}</h4>
                  <div className="kpi-sub">Descuento financiera</div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-green">🏦</div>
                  <small>Monto financiera</small>
                  <h4 className="text-neto">
                    {formatUSD(totales.totalMontoFinancieraUSD)}
                  </h4>
                  <div className="kpi-sub">Neto esperado</div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-yellow">⏳</div>
                  <small>Pend. acreditar</small>
                  <h4>{formatUSD(totales.totalPendienteAcreditarUSD)}</h4>
                  <div className="kpi-sub">Pagos pendientes</div>
                </Card.Body>
              </Card>
            </Col>

            <Col>
              <Card className="dashboard-kpi">
                <Card.Body>
                  <div className="kpi-icon kpi-purple">💰</div>
                  <small>Saldo pendiente</small>
                  <h4>{formatUSD(totales.saldoPendienteUSD)}</h4>
                  <div className="kpi-sub">Venta sin cubrir</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Totales entre fechas</h6>
                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={serieEntreFechas}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis />
                        <Tooltip formatter={(v) => formatUSD(v)} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="ventas"
                          name="Venta total"
                          stroke="#3b82f6"
                          strokeWidth={2}
                        />

                        <Line
                          type="monotone"
                          dataKey="tax"
                          name="Tax"
                          stroke="#2563eb"
                          strokeWidth={2}
                        />

                        <Line
                          type="monotone"
                          dataKey="sinTax"
                          name="Venta sin tax"
                          stroke="#0ea5e9"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="cobrado"
                          name="Cobrado"
                          stroke="#22c55e"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="fee"
                          name="Fee retenido"
                          stroke="#ef4444"
                          strokeWidth={3}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Ventas por financiera</h6>
                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ventasPorFinanciera}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="financiera" />
                        <YAxis />
                        <Tooltip formatter={(v) => formatUSD(v)} />
                        <Legend />
                          <Bar dataKey="total" name="Cargado" fill="#3b82f6" />
                          <Bar dataKey="neto" name="Monto financiera" fill="#22c55e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={3}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Fee retenido por financiera</h6>
                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={feePorFinanciera}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="financiera" />
                        <YAxis />
                        <Tooltip formatter={(v) => formatUSD(v)} />
                        <Bar dataKey="fee" name="Fee retenido" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={4}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Estado de acreditación</h6>
                  <div className="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={donutEstado}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={70}
                          outerRadius={100}
                          label
                        >
                          {donutEstado.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatUSD(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Top financieras por ventas</h6>
                  <div className="table-responsive">
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Financiera</th>
                          <th className="text-end">Cargado</th>
                          <th className="text-end text-fee">Fee</th>
                          <th className="text-end text-neto">Neto</th>
                          <th className="text-end">% fee</th>
                          <th className="text-end">Mov.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topFinancieras.map((f) => (
                          <tr key={f.financiera}>
                            <td>{f.index}</td>
                            <td>{f.financiera}</td>
                            <td className="text-end">{formatUSD(f.total)}</td>
                            <td className="text-end text-fee">{formatUSD(f.fee)}</td>
                            <td className="text-end text-neto">{formatUSD(f.neto)}</td>
                            <td className="text-end">{f.porcentajeFee}%</td>
                            <td className="text-end">{f.ventas}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="dashboard-panel">
                <Card.Body>
                  <h6>Últimos movimientos</h6>
                  <div className="table-responsive">
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Cliente</th>
                          <th>Financiera</th>
                          <th className="text-end">Monto</th>
                          <th className="text-end text-fee">Fee</th>
                          <th className="text-end text-neto">Neto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ultimosMovimientos.map((m, idx) => (
                          <tr key={`${m.ventaId}-${m.pagoId}-${idx}`}>
                            <td>{formatDate(m.fechaAcreditacion || m.fechaPago)}</td>
                            <td>{nombreClienteCompra(m.clienteCompra)}</td>
                            <td>{m.financieraNombre || "-"}</td>
                            <td className="text-end">{formatUSD(m.montoUSD)}</td>
                            <td className="text-end text-fee">
                              {esPagoCreditoFinanciera(m) ? formatUSD(m.feeMontoUSD) : "-"}
                            </td>
                            <td className="text-end text-neto">
                              {esPagoCreditoFinanciera(m) ? formatUSD(m.montoFinancieraUSD) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}